import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '../../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../ui/select';
import { Scissors, AlertCircle, Loader2 } from 'lucide-react';

export default function ModalDividirLote({
  open,
  onOpenChange,
  lote,
  categoriaDivisao,
  setCategoriaDivisao,
  numeroLotesDivisao,
  setNumeroLotesDivisao,
  quantidadePorLoteDivisao,
  setQuantidadePorLoteDivisao,
  observacoesDivisao,
  setObservacoesDivisao,
  erroDividir,
  saving,
  categoriasFiltradas,
  getCategoriaCor,
  onConfirm
}) {
  const pecasDisponiveis = (lote?.quantidade || 0) - (lote?.quantidade_alocada_seguinte || 0);
  const totalDivisao = numeroLotesDivisao * parseInt(quantidadePorLoteDivisao || 0);
  const excedeDisponivel = totalDivisao > pecasDisponiveis;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-amber-600" />
            Dividir Lote
          </DialogTitle>
          <DialogDescription>
            Criar novos lotes a partir de <strong>{lote?.codigo_lote || `L${lote?.numero_lote}`}</strong>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {lote && (
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-stone-700">Lote Origem</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${getCategoriaCor(lote.categoria_codigo)}`}>
                  {lote.categoria_nome || 'Sem categoria'}
                </span>
              </div>
              <p className="text-2xl font-bold text-stone-900">
                {pecasDisponiveis} 
                <span className="text-sm font-normal"> peças disponíveis para alocar</span>
              </p>
              {(lote.quantidade_alocada_seguinte || 0) > 0 && (
                <p className="text-xs text-stone-500 mt-1">
                  Total: {lote.quantidade} peças | Já alocado: {lote.quantidade_alocada_seguinte} peças
                </p>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-1">
              Categoria de destino
            </label>
            <Select value={categoriaDivisao} onValueChange={setCategoriaDivisao}>
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Seleccione a categoria de destino" />
              </SelectTrigger>
              <SelectContent>
                {categoriasFiltradas.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="font-mono text-xs bg-stone-100 px-1 rounded mr-2">{cat.codigo}</span>
                    {cat.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Número de lotes a criar *
              </label>
              <Input
                type="number"
                min="1"
                max="20"
                value={numeroLotesDivisao}
                onChange={(e) => setNumeroLotesDivisao(parseInt(e.target.value) || 1)}
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Quantidade por lote *
              </label>
              <Input
                type="number"
                min="1"
                value={quantidadePorLoteDivisao}
                onChange={(e) => setQuantidadePorLoteDivisao(e.target.value)}
                placeholder="Peças por lote"
                className="h-12"
                data-testid="input-quantidade-dividir"
              />
            </div>
          </div>
          
          {quantidadePorLoteDivisao && parseInt(quantidadePorLoteDivisao) > 0 && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm">
                <strong>Resumo:</strong> Serão criados <strong>{numeroLotesDivisao}</strong> lotes 
                de <strong>{quantidadePorLoteDivisao}</strong> peças cada = <strong>{totalDivisao}</strong> peças total
              </p>
              {excedeDisponivel && (
                <p className="text-sm text-red-600 mt-1">
                  Quantidade excede o disponível ({pecasDisponiveis} peças)
                </p>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-1">
              Observações (opcional)
            </label>
            <Textarea
              value={observacoesDivisao}
              onChange={(e) => setObservacoesDivisao(e.target.value)}
              placeholder="Ex: Divisão para envio separado"
              rows={2}
            />
          </div>
          
          {erroDividir && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">{erroDividir}</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={saving || !quantidadePorLoteDivisao || parseInt(quantidadePorLoteDivisao) <= 0 || excedeDisponivel}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="btn-confirmar-dividir"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Scissors className="h-4 w-4 mr-2" />
            )}
            Criar {numeroLotesDivisao} Lote{numeroLotesDivisao > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
